import React, { useState, useEffect, useRef } from 'react'
import { useTranslate, useNotify, useRefresh, useShowContext } from 'react-admin'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  Grid,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import { LoadingButton } from '@mui/lab'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import dayjs from 'dayjs'
import { supplierServices } from 'src/services/reservation/supplier/supplier.service'
import { itemServices } from 'src/services/reservation/item/item.service'
import { clientServices } from 'src/services/auth/client/client.service'
import { productServices } from 'src/services/reservation/product/product.service'

export const ComplementaryItemModal = ({
  open,
  hashItem,
  onClose,
  reservation,
  selectedProduct,
  onSubmitSuccess,
}) => {
  const translate = useTranslate()
  const notify = useNotify()
  const refresh = useRefresh()
  const showContext = useShowContext()
  const currentReservation = reservation || showContext?.record

  const [loading, setLoading] = useState(false)
  const [supplierId, setSupplierId] = useState(null)
  const [supplierOptions, setSupplierOptions] = useState([])
  const [supplierLoading, setSupplierLoading] = useState(false)
  const [code, setCode] = useState('Servicios varios')
  const [name, setName] = useState('Servicios adicionales')
  const [serviceDetail, setServiceDetail] = useState('Servicios varios')

  const searchTimeoutRef = useRef(null)

  const resolveTargetHashItem = (items) => {
    if (!items || items.length === 0) return null

    const activeItems = items.filter(
      (item) => item.status !== 'DELETED' && item.hash_
    )

    if (activeItems.length === 0) return null

    const todayStart = dayjs().startOf('day').valueOf()

    // 1. Ítems con date_in >= hoy, ordenados ascendentemente (el más cercano a hoy primero)
    const futureOrTodayItems = activeItems
      .filter((item) => item.date_in && dayjs(item.date_in).startOf('day').valueOf() >= todayStart)
      .sort((a, b) => dayjs(a.date_in).valueOf() - dayjs(b.date_in).valueOf())

    if (futureOrTodayItems.length > 0) {
      return futureOrTodayItems[0].hash_
    }

    // 2. Si todos tienen date_in en el pasado, tomar el más cercano a hoy
    const itemsWithDate = activeItems
      .filter((item) => item.date_in)
      .sort(
        (a, b) =>
          Math.abs(dayjs(a.date_in).valueOf() - todayStart) -
          Math.abs(dayjs(b.date_in).valueOf() - todayStart)
      )

    if (itemsWithDate.length > 0) {
      return itemsWithDate[0].hash_
    }

    // 3. Fallback al primer ítem activo
    return activeItems[0]?.hash_ || null
  }

  useEffect(() => {
    if (open) {
      setSupplierId(null)
      setSupplierOptions([])
      setCode('Servicios varios')
      setName('Servicios adicionales')
      setServiceDetail('Servicios varios')

      const hashClient =
        currentReservation?.work_unit_relation?.client_relation?.hash_client ||
        currentReservation?.business_unit_relation?.client_relation?.hash_client ||
        currentReservation?.client_relation?.hash_client

      setSupplierLoading(true)

      const fetchSuppliers = supplierServices
        .getByParam({ limit: 999 })
        .then((res) => (Array.isArray(res) ? res : []))
        .catch((err) => {
          console.error('Error fetching suppliers list:', err)
          return []
        })

      const fetchClientConfig = hashClient
        ? clientServices
            .getByHashClient(hashClient)
            .then(async (clientData) => {
              const config = clientData?.applicationByClient?.[0]?.config
              const createItemConfigs = config?.autocomplete_forms?.create_item
              if (!Array.isArray(createItemConfigs) || createItemConfigs.length === 0) return null

              let targetHashProduct = selectedProduct?.product_relation?.hash_product

              // Flujo normal (botón dentro del ítem): obtener hash_product del producto configurado para ComplementaryItemModal
              if (!targetHashProduct) {
                const workUnitId =
                  currentReservation?.work_unit_id || currentReservation?.work_unit_relation?.id

                if (workUnitId) {
                  try {
                    const wuProducts = await productServices.getProductByWorkUnitId(workUnitId, true)
                    const compProduct = wuProducts.find((p) => {
                      const pHash =
                        p?.product_relation?.hash_product || p?.product?.hash_product || p?.hash_product
                      const pTable = p?.product_relation?.ref_table_detail || p?.ref_table_detail
                      const rawParams =
                        p?.product_relation?.parameters || p?.product?.parameters || p?.parameters
                      let params = {}
                      if (typeof rawParams === 'string') {
                        try {
                          params = JSON.parse(rawParams)
                        } catch (e) {}
                      } else if (typeof rawParams === 'object' && rawParams !== null) {
                        params = rawParams
                      }
                      return (
                        params?.offline_create_form === 'ComplementaryItemModal' ||
                        (pTable === 'other_detail' &&
                          createItemConfigs.some((c) => c.hash_product === pHash))
                      )
                    })

                    if (compProduct) {
                      targetHashProduct =
                        compProduct?.product_relation?.hash_product ||
                        compProduct?.product?.hash_product ||
                        compProduct?.hash_product
                    }
                  } catch (e) {
                    console.error('Error fetching work unit products:', e)
                  }
                }
              }

              let matchedConfig = null
              if (targetHashProduct) {
                matchedConfig = createItemConfigs.find(
                  (entry) => entry?.hash_product === targetHashProduct
                )
              }

              if (!matchedConfig && createItemConfigs.length === 1) {
                matchedConfig = createItemConfigs[0]
              }

              return matchedConfig
            })
            .catch((error) => {
              console.error('Error fetching client autocomplete config:', error)
              return null
            })
        : Promise.resolve(null)

      Promise.all([fetchSuppliers, fetchClientConfig])
        .then(([suppliersList, matchedConfig]) => {
          setSupplierOptions(suppliersList)

          if (matchedConfig?.fields) {
            const {
              name: configName,
              supplier_id: configSupplierId,
              service_detail: configServiceDetail,
              code: configCode,
            } = matchedConfig.fields

            if (configName !== undefined && configName !== null) {
              setName(configName)
            }

            if (configCode !== undefined && configCode !== null) {
              setCode(configCode)
            }

            if (configServiceDetail !== undefined && configServiceDetail !== null) {
              setServiceDetail(configServiceDetail)
            }

            if (configSupplierId) {
              const sId = Number(configSupplierId)
              const matchedSupplier = suppliersList.find((s) => Number(s.id) === sId)
              if (matchedSupplier) {
                setSupplierId(matchedSupplier.id)
              } else {
                setSupplierId(null)
              }
            } else {
              setSupplierId(null)
            }
          }
        })
        .finally(() => {
          setSupplierLoading(false)
        })
    }
  }, [open, currentReservation?.id, selectedProduct?.id, selectedProduct?.product_relation?.id])

  const getSupplier = (name) => {
    clearTimeout(searchTimeoutRef.current)
    let params = {}
    if (name) {
      if (Number.isNaN(Number(name))) {
        params.name = name
      } else {
        params.id = Number(name)
      }
    }
    params.limit = 999
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setSupplierLoading(true)
        const results = await supplierServices.getByParam(params)
        setSupplierOptions(Array.isArray(results) ? results : [])
        setSupplierLoading(false)
      } catch (error) {
        setSupplierOptions([])
        setSupplierLoading(false)
      }
    }, 700)
  }

  const handleSubmit = async () => {
    const finalHash = hashItem || resolveTargetHashItem(currentReservation?.items)
    if (!finalHash) {
      notify('ra.message.no_item_to_link_complementary', { type: 'error' })
      return
    }

    setLoading(true)
    try {
      await itemServices.complementaryItem(finalHash, {
        supplier_id: supplierId,
        code,
        name: name,
        service_detail: serviceDetail,
      })
      notify('ra.message.complementary_item_success', { type: 'success' })
      onClose()
      refresh()
      if (onSubmitSuccess) {
        onSubmitSuccess()
      }
    } catch (error) {
      notify('ra.message.complementary_item_error', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const selectedSupplier =
    supplierOptions.find((s) => Number(s.id) === Number(supplierId)) || null

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{translate('ra.title.complementary_item')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <EditInputsWrap label={translate('ra.field.supplier')} md={12}>
            <Autocomplete
              options={supplierOptions}
              getOptionLabel={(option) => (option ? `${option.id} - ${option.name}` : '')}
              isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
              loading={supplierLoading}
              value={selectedSupplier}
              onChange={(e, newValue) => setSupplierId(newValue?.id ?? null)}
              onInputChange={(e, newInputValue, reason) => {
                if (reason === 'input') {
                  getSupplier(newInputValue)
                } else if (reason === 'clear') {
                  getSupplier('')
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size='small'
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {supplierLoading ? <CircularProgress color='inherit' size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </EditInputsWrap>
          <EditInputsWrap
            label={translate('ra.field.locator')}
            tooltiptext='Detalle del servicio que se refleja en la factura.'
            md={6}
          >
            <TextField
              value={code}
              onChange={(e) => setCode(e.target.value)}
              size='small'
              fullWidth
            />
          </EditInputsWrap>
          <EditInputsWrap
            label={translate('ra.field.name')}
            required
            tooltiptext='Detalle que se refleja en el nombre del item.'
            md={6}
          >
            <TextField
              value={name}
              onChange={(e) => setName(e.target.value)}
              size='small'
              fullWidth
            />
          </EditInputsWrap>
          <EditInputsWrap label={translate('ra.field.service_detail')} md={12}>
            <TextField
              value={serviceDetail}
              onChange={(e) => setServiceDetail(e.target.value)}
              size='small'
              fullWidth
              multiline
              minRows={3}
            />
          </EditInputsWrap>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant='outlined' color='error' disabled={loading}>
          {translate('ra.action.cancel')}
        </Button>
        <LoadingButton
          loading={loading}
          variant='contained'
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          disabled={!supplierId}
        >
          {translate('ra.action.save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
